import React, { useMemo } from 'react';
import { PriceChangeType } from 'app/blocks/article-affiliations-editor/types';
import { EditorAffiliation } from 'app/blocks/article-affiliations-editor/utils';
import { ID, withCodes } from 'app/blocks/common/codes';
import FundingInformation from './FundingInformation/FundingInformation';
import { getAffiliationValue, getPrimaryInstitutionName, getFundingInfoConfigs } from './utils';
import './confirmPriceChange.scss';

type Props = {
    readonly affiliations: EditorAffiliation[];
    readonly eligibleAffiliations: EditorAffiliation[];
    readonly priceChangeType: PriceChangeType;
    readonly selectedPrimaryAffiliationId?: string;
    readonly rejectionReasonAccountBalanceNotEnough?: boolean;
    readonly l: l;
};

function ConfirmPriceChange({
    affiliations,
    eligibleAffiliations,
    l,
    priceChangeType,
    rejectionReasonAccountBalanceNotEnough,
    selectedPrimaryAffiliationId,
}: Props) {
    const seleniumid = 'article-affiliations-editor-confirm-price-change';

    const primaryInstitutionName = useMemo(
        () => getPrimaryInstitutionName(eligibleAffiliations, selectedPrimaryAffiliationId),
        [eligibleAffiliations, selectedPrimaryAffiliationId],
    );

    const affiliationValue = useMemo(
        () =>
            getAffiliationValue({
                affiliations,
                l,
                seleniumid,
                showAllInline: priceChangeType === PriceChangeType.FUNDER_TO_AUTHOR_INSTITUTIONAL_DISCOUNT,
            }),
        [affiliations, l, priceChangeType, seleniumid],
    );

    const fundingInfoConfigs = useMemo(
        () =>
            getFundingInfoConfigs({
                affiliationValue,
                l,
                priceChangeType,
                primaryInstitutionName,
                rejectionReasonAccountBalanceNotEnough,
                seleniumid,
            }),
        [
            affiliationValue,
            l,
            priceChangeType,
            primaryInstitutionName,
            rejectionReasonAccountBalanceNotEnough,
            seleniumid,
        ],
    );

    return (
        <div className="confirmPriceChange" data-seleniumid={seleniumid}>
            {fundingInfoConfigs.map((props, idx) => (
                <FundingInformation key={props.seleniumid || idx} {...props} />
            ))}
        </div>
    );
}

ConfirmPriceChange.defaultProps = {
    rejectionReasonAccountBalanceNotEnough: undefined,
    selectedPrimaryAffiliationId: undefined,
};

export default withCodes(ConfirmPriceChange, ID.ARTICLE_AFFILIATION_EDITOR);
